/**
 * 🛗 [Phase 22 Bug 3 — 2026-05-31] formatNacelleDisplay()
 * ========================================================
 * Helper unique pour l'affichage cohérent du numéro + nom des nacelles
 * partout dans l'application (aperçu BT, calendrier, dashboards, PDFs, SMS…).
 *
 * Le problème : auparavant les composants affichaient `nacelle.nom` ou
 * `equipement_nom` seul, sans le numéro d'unité (#U-001). Sur le terrain,
 * impossible de savoir quelle nacelle physique prendre.
 *
 * Usage :
 *   import { formatNacelleDisplay } from '@/lib/formatNacelle';
 *
 *   // Contextes UI standards (web admin, dashboard) :
 *   formatNacelleDisplay({numero_unite:'U-001', nom:'Genie Z45'})
 *     → "🛗 #U-001 – Genie Z45"
 *
 *   // Contexte PDF / SMS / impression (pas d'emoji unicode parfois cassé) :
 *   formatNacelleDisplay(nacelle, { emoji: false })
 *     → "#U-001 – Genie Z45"
 *
 *   // Format compact (vignettes, liste serrée) :
 *   formatNacelleDisplay(nacelle, { compact: true })
 *     → "#U-001 Genie Z45"
 *
 *   // Si numéro absent → fallback explicite :
 *   formatNacelleDisplay({nom:'Genie Z45'})
 *     → "🛗 Genie Z45 (n° manquant)"
 *
 * Contextes recommandés par option :
 *   - emoji=true (défaut) : UI web, mobile, popovers, badges
 *   - emoji=false : PDF, SMS, exports CSV, contrats imprimés
 *   - compact=true : tableaux denses, sélecteurs déroulants
 */

/**
 * Retourne une chaîne d'affichage formatée pour une nacelle.
 * @param {object|null} nacelle - { numero_unite?, numero?, nom?, equipement_nom?, modele? }
 * @param {object} [options]
 * @param {boolean} [options.emoji=true] - inclure l'emoji 🛗 (false pour PDF/SMS)
 * @param {boolean} [options.compact=false] - format compact sans tiret moyen
 * @returns {string}
 */
export function formatNacelleDisplay(nacelle, options = {}) {
  const { emoji = true, compact = false } = options;
  if (!nacelle) return emoji ? '🛗 Nacelle' : 'Nacelle';

  const num = nacelle.numero_unite || nacelle.numero || nacelle.equipements_nacelle_numero_unite;
  const nom = nacelle.nom
    || nacelle.equipement_nom
    || nacelle.equipements_nacelle_nom
    || nacelle.modele
    || 'Nacelle';

  const prefix = emoji ? '🛗 ' : '';

  if (!num) {
    return `${prefix}${nom} (n° manquant)`;
  }
  if (compact) {
    return `${prefix}#${num} ${nom}`.trim();
  }
  return `${prefix}#${num} – ${nom}`;
}

/**
 * Extrait juste le numéro d'unité d'un objet nacelle (helper utilitaire).
 * Utile pour les exports CSV où on veut le n° seul.
 */
export function getNacelleNumero(nacelle) {
  if (!nacelle) return '';
  return nacelle.numero_unite || nacelle.numero || nacelle.equipements_nacelle_numero_unite || '';
}
